import { useQueryClient } from "@tanstack/react-query";
import { VITE_SITE_SERVICE_BASE_URL } from "../../../env";
import { CamelCasedProperties } from "type-fest";
import {
  recursiveCamelCaseCipher,
  recursiveSnakeCaseCipher,
} from "../../postgrestApi";
import request from "../../request";
import { ClientOutput } from "../client";
import {
  CheckFeatureAccessHeader,
  useMutationWithAccessCheckLog,
  useQueryWithAccessCheckLog,
} from "@src/auth/permissions/usePreRequestFeatureAccessCheck";

const vendors = {
  useQuery,
  usePost,
  usePatch,
  useDelete,
};

export default vendors;

const QUERY_KEY = "vendors";

export type Vendor = CamelCasedProperties<ClientOutput["vendors"]["Vendor"]>;
export type VendorType = CamelCasedProperties<
  ClientOutput["vendors"]["VendorType"]
>;
type VendorAdd = CamelCasedProperties<ClientOutput["vendors"]["VendorAdd"]>;
export type VendorEdit = CamelCasedProperties<
  ClientOutput["vendors"]["VendorEdit"]
>;

// endpoint requires {feature: "Building Overview", access: "read"}
const getVendors = (
  siteId: number,
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
): Promise<Vendor[]> =>
  request
    .get(`${VITE_SITE_SERVICE_BASE_URL}/vendors`)
    .set(checkFeatureAccessHeader)
    .query({
      site_id: siteId,
    })
    .then((res) => recursiveCamelCaseCipher(res.body));

function useQuery(siteId: number) {
  return useQueryWithAccessCheckLog(
    ["Building Overview", "read"],
    (checkFeatureAccessHeader) =>
      getVendors(siteId, { checkFeatureAccessHeader }),
    { queryKey: [QUERY_KEY, siteId] }
  );
}

const useInvalidateVendorsQuery = (siteId: number) => {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: [QUERY_KEY, siteId],
    });
};

// endpoint requires {feature: "Building Overview", access: "edit"}
const postVendor = (
  vendor: VendorAdd,
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
) =>
  request
    .post(`${VITE_SITE_SERVICE_BASE_URL}/vendors`)
    .set(checkFeatureAccessHeader)
    .send(recursiveSnakeCaseCipher(vendor));

function usePost(siteId: number) {
  const invalidateVendorsQuery = useInvalidateVendorsQuery(siteId);

  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (vendor: Omit<VendorAdd, "siteId">, checkFeatureAccessHeader) =>
      postVendor({ ...vendor, siteId }, { checkFeatureAccessHeader }),
    { onSuccess: async () => await invalidateVendorsQuery() }
  );
}

// endpoint requires {feature: "Building Overview", access: "edit"}
const patchVendor = (
  vendorId: number,
  vendor: VendorEdit,
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
) =>
  request
    .patch(`${VITE_SITE_SERVICE_BASE_URL}/vendors/${vendorId}`)
    .set(checkFeatureAccessHeader)
    .send(recursiveSnakeCaseCipher(vendor));

function usePatch(siteId: number) {
  const invalidateVendorsQuery = useInvalidateVendorsQuery(siteId);
  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (
      {
        vendorId,
        vendor,
      }: {
        vendorId: number;
        vendor: VendorEdit;
      },
      checkFeatureAccessHeader
    ) => patchVendor(vendorId, vendor, { checkFeatureAccessHeader }),
    { onSuccess: async () => await invalidateVendorsQuery() }
  );
}

// endpoint requires {feature: "Building Overview", access: "edit"}
const deleteVendor = (
  vendorId: number,
  {
    checkFeatureAccessHeader,
  }: { checkFeatureAccessHeader: CheckFeatureAccessHeader }
) => {
  return request
    .delete(`${VITE_SITE_SERVICE_BASE_URL}/vendors/${vendorId}`)
    .set(checkFeatureAccessHeader);
};

function useDelete(siteId: number) {
  const invalidateVendorsQuery = useInvalidateVendorsQuery(siteId);
  return useMutationWithAccessCheckLog(
    ["Building Overview", "edit"],
    (vendorId: number, checkFeatureAccessHeader) =>
      deleteVendor(vendorId, { checkFeatureAccessHeader }),
    { onSuccess: async () => await invalidateVendorsQuery() }
  );
}
